import { useRef, useState } from 'react';
import { TabStrip } from './TabStrip';
import { getViewMeta } from './ViewRegistry';
import type { DragState, DropTarget, LeafNode, OpenTabFn, OpenTabOptions } from './types';

type Edge = 'left' | 'right' | 'top' | 'bottom' | 'center';

interface Props {
  leaf: LeafNode;
  activeLeafId: string | null;
  setActiveLeafId: (id: string) => void;
  onClose: (leafId: string, tabId: string) => void;
  onPin: (leafId: string, tabId: string) => void;
  onActivate: (leafId: string, tabId: string) => void;
  onDragEnd: (sourceLeafId: string, tabId: string, target: DropTarget) => void;
  dragState: DragState | null;
  setDragState: (s: DragState | null) => void;
  openTabInLeaf: (
    leafId: string,
    viewId: string,
    payload?: Record<string, unknown>,
    label?: string,
    opts?: OpenTabOptions,
  ) => void;
}

export function LeafPane({
  leaf,
  activeLeafId,
  setActiveLeafId,
  onClose,
  onPin,
  onActivate,
  onDragEnd,
  dragState,
  setDragState,
  openTabInLeaf,
}: Props) {
  const active = activeLeafId === leaf.id;
  const [hoverEdge, setHoverEdge] = useState<Edge | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const activeTab = leaf.tabs.find((t) => t.id === leaf.activeId) ?? leaf.tabs[0];
  const meta = activeTab ? getViewMeta(activeTab.viewId) : undefined;

  const onContentDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragState || !contentRef.current) return;
    e.preventDefault();
    const rect = contentRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const thresh = 0.2;
    let edge: Edge;
    if (x < thresh) edge = 'left';
    else if (x > 1 - thresh) edge = 'right';
    else if (y < thresh) edge = 'top';
    else if (y > 1 - thresh) edge = 'bottom';
    else edge = 'center';
    setHoverEdge(edge);
  };

  const onContentDragLeave = () => setHoverEdge(null);

  const onContentDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragState) {
      setHoverEdge(null);
      return;
    }
    const edge = hoverEdge;
    setHoverEdge(null);
    if (edge === 'center' || !edge) {
      onDragEnd(dragState.sourceLeafId, dragState.tabId, { type: 'leaf-tabs', leafId: leaf.id });
    } else {
      onDragEnd(dragState.sourceLeafId, dragState.tabId, { type: 'leaf-split', leafId: leaf.id, edge });
    }
    setDragState(null);
  };

  const edgeOverlay =
    hoverEdge && hoverEdge !== 'center'
      ? ({
          left: { left: 0, top: 0, width: '50%', height: '100%' },
          right: { right: 0, top: 0, width: '50%', height: '100%' },
          top: { left: 0, top: 0, width: '100%', height: '50%' },
          bottom: { left: 0, bottom: 0, width: '100%', height: '50%' },
        } as const)[hoverEdge]
      : null;

  const openTabForLeaf: OpenTabFn = (viewId, payload, label, opts) =>
    openTabInLeaf(leaf.id, viewId, payload, label, opts);

  return (
    <div
      onMouseDown={() => setActiveLeafId(leaf.id)}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        border: active ? '1px solid var(--accent-soft)' : '1px solid transparent',
        borderRadius: 2,
      }}
    >
      <TabStrip
        leaf={leaf}
        onClose={onClose}
        onPin={onPin}
        onActivate={onActivate}
        onDragEnd={onDragEnd}
        dragState={dragState}
        setDragState={setDragState}
      />
      <div
        ref={contentRef}
        onDragOver={onContentDragOver}
        onDragLeave={onContentDragLeave}
        onDrop={onContentDrop}
        style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden', background: 'var(--bg)' }}
      >
        {activeTab && meta ? (
          meta.render({ payload: activeTab.payload, openTab: openTabForLeaf })
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
            Drop a tab here
          </div>
        )}
        {edgeOverlay && (
          <div
            style={{
              position: 'absolute',
              ...edgeOverlay,
              background: 'var(--accent-soft)',
              border: '2px dashed var(--accent)',
              pointerEvents: 'none',
              zIndex: 5,
              borderRadius: 4,
            }}
          />
        )}
        {hoverEdge === 'center' && (
          <div
            style={{
              position: 'absolute',
              inset: 8,
              background: 'var(--accent-soft)',
              border: '2px dashed var(--accent)',
              pointerEvents: 'none',
              zIndex: 5,
              borderRadius: 6,
            }}
          />
        )}
      </div>
    </div>
  );
}
